/**
 * useBranding - read the white-label config and push it into CSS variables.
 *
 * Variables set on `<html>`:
 *   --brand-primary / --brand-secondary / --brand-accent
 *   --brand-background / --brand-surface
 *   --brand-radius-sm / --brand-radius-md / --brand-radius-lg
 *   --brand-font-family
 *
 * Also sets `data-` attributes on `<html>` for opt-in behavior:
 *   data-gradient="on"           → globals.css applies gradient on .btn-brand-primary
 *   data-bg-image="<url>"        → main element shows background image
 *
 * Loads the chosen Google Font as a stylesheet `<link>` once, swapping the
 * font without breaking layout (uses CSS `font-display: swap`).
 */

import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiUrl } from "./api-client";
import { useTheme } from "./use-theme";

// Dark-mode overrides for the two surfaces. Brand primary/secondary/accent
// stay as the template defined them so the vertical's identity carries
// through. Only the page chrome (background + cards) switches.
const DARK_BACKGROUND = "#0b1220";
const DARK_SURFACE = "#111827";

const DEFAULT_BRAND = "AI Leasing OS";
const DEFAULT_PRIMARY = "#0f172a";
const DEFAULT_SECONDARY = "#1e293b";
const DEFAULT_ACCENT = "#475569";
const DEFAULT_BACKGROUND = "#f8fafc";
const DEFAULT_SURFACE = "#ffffff";
const DEFAULT_FONT = "Inter";
const DEFAULT_RADIUS: RadiusKey = "subtle";

export type RadiusKey = "square" | "subtle" | "medium" | "rounded" | "pill";

const RADIUS_MAP: Record<RadiusKey, { sm: string; md: string; lg: string }> = {
  square:  { sm: "0",       md: "0",       lg: "0"       },
  subtle:  { sm: "0.25rem", md: "0.375rem", lg: "0.5rem" },
  medium:  { sm: "0.375rem", md: "0.625rem", lg: "0.875rem" },
  rounded: { sm: "0.5rem",  md: "0.875rem", lg: "1.25rem" },
  pill:    { sm: "9999px",  md: "9999px",  lg: "1.5rem"  },
};

/** Curated Google Fonts list - covers most common dashboard vibes. */
export const FONT_OPTIONS: { value: string; label: string; googleSpec?: string }[] = [
  { value: "Inter",            label: "Inter (default)" },
  { value: "DM Sans",          label: "DM Sans" },
  { value: "Plus Jakarta Sans",label: "Plus Jakarta Sans" },
  { value: "Poppins",          label: "Poppins" },
  { value: "Manrope",          label: "Manrope" },
  { value: "Outfit",           label: "Outfit" },
  { value: "Space Grotesk",    label: "Space Grotesk" },
  { value: "Work Sans",        label: "Work Sans" },
  { value: "Nunito",           label: "Nunito (rounder)" },
  { value: "Lato",             label: "Lato (classic)" },
  { value: "JetBrains Mono",   label: "JetBrains Mono (mono)" },
];

export interface Branding {
  name: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  surfaceColor: string;
  fontFamily: string;
  radius: RadiusKey;
  useGradients: boolean;
  backgroundImageUrl: string | null;
  backgroundImageOverlay: string | null;
}

interface BrandingResponse {
  ok: boolean;
  data: {
    app_brand_name: string;
    business_name: string | null;
    brand_logo_url: string | null;
    brand_favicon_url: string | null;
    brand_primary_color: string | null;
    brand_secondary_color: string | null;
    brand_accent_color: string | null;
    brand_background_color: string | null;
    brand_surface_color: string | null;
    brand_font_family: string | null;
    brand_radius: string | null;
    brand_use_gradients: number | null;
    brand_background_image_url: string | null;
    brand_background_image_overlay: string | null;
  };
}

export const BRANDING_QUERY_KEY = ["branding"] as const;

async function fetchBranding(): Promise<Branding> {
  try {
    const res = await fetch(apiUrl("/api/branding"));
    if (!res.ok) return defaultBranding();
    const payload = (await res.json()) as BrandingResponse;
    const d = payload?.data;
    const rawRadius = (d?.brand_radius ?? "").trim();
    const radius: RadiusKey = (rawRadius in RADIUS_MAP ? rawRadius : DEFAULT_RADIUS) as RadiusKey;
    return {
      name: d?.app_brand_name?.trim() || DEFAULT_BRAND,
      logoUrl: d?.brand_logo_url?.trim() || null,
      faviconUrl: d?.brand_favicon_url?.trim() || null,
      primaryColor: d?.brand_primary_color?.trim() || DEFAULT_PRIMARY,
      secondaryColor: d?.brand_secondary_color?.trim() || DEFAULT_SECONDARY,
      accentColor: d?.brand_accent_color?.trim() || DEFAULT_ACCENT,
      backgroundColor: d?.brand_background_color?.trim() || DEFAULT_BACKGROUND,
      surfaceColor: d?.brand_surface_color?.trim() || DEFAULT_SURFACE,
      fontFamily: d?.brand_font_family?.trim() || DEFAULT_FONT,
      radius,
      useGradients: Number(d?.brand_use_gradients ?? 0) === 1,
      backgroundImageUrl: d?.brand_background_image_url?.trim() || null,
      backgroundImageOverlay: d?.brand_background_image_overlay?.trim() || null,
    };
  } catch {
    return defaultBranding();
  }
}

function defaultBranding(): Branding {
  return {
    name: DEFAULT_BRAND,
    logoUrl: null,
    faviconUrl: null,
    primaryColor: DEFAULT_PRIMARY,
    secondaryColor: DEFAULT_SECONDARY,
    accentColor: DEFAULT_ACCENT,
    backgroundColor: DEFAULT_BACKGROUND,
    surfaceColor: DEFAULT_SURFACE,
    fontFamily: DEFAULT_FONT,
    radius: DEFAULT_RADIUS,
    useGradients: false,
    backgroundImageUrl: null,
    backgroundImageOverlay: null,
  };
}

/** Lazy-load a Google Font stylesheet exactly once per font family. */
function ensureGoogleFontLoaded(family: string): void {
  if (!family || family === "Inter") return; // Inter is bundled via tailwind defaults
  const id = `gfont-${family.replace(/\W+/g, "-").toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    family,
  )}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

export function useBranding(): Branding {
  const { data } = useQuery({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: fetchBranding,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const branding = data ?? defaultBranding();
  const theme = useTheme((s) => s.theme);

  useEffect(() => {
    const root = document.documentElement;
    const isDark = theme === "dark";

    // Colors. Brand primary/secondary/accent always come from the template;
    // only background + surface get a dark override when the user toggled
    // dark mode.
    root.style.setProperty("--brand-primary", branding.primaryColor);
    root.style.setProperty("--brand-secondary", branding.secondaryColor);
    root.style.setProperty("--brand-accent", branding.accentColor);
    root.style.setProperty(
      "--brand-background",
      isDark ? DARK_BACKGROUND : branding.backgroundColor,
    );
    root.style.setProperty(
      "--brand-surface",
      isDark ? DARK_SURFACE : branding.surfaceColor,
    );

    // Radius scale
    const r = RADIUS_MAP[branding.radius];
    root.style.setProperty("--brand-radius-sm", r.sm);
    root.style.setProperty("--brand-radius-md", r.md);
    root.style.setProperty("--brand-radius-lg", r.lg);

    // Font
    ensureGoogleFontLoaded(branding.fontFamily);
    root.style.setProperty(
      "--brand-font-family",
      `"${branding.fontFamily}", Inter, ui-sans-serif, system-ui, sans-serif`,
    );

    // Opt-in flags (data attributes - globals.css reacts to them)
    if (branding.useGradients) {
      root.setAttribute("data-gradient", "on");
    } else {
      root.removeAttribute("data-gradient");
    }

    if (branding.backgroundImageUrl) {
      root.setAttribute("data-bg-image", "on");
      root.style.setProperty(
        "--brand-bg-image",
        `url("${branding.backgroundImageUrl.replace(/"/g, "%22")}")`,
      );
      root.style.setProperty(
        "--brand-bg-overlay",
        branding.backgroundImageOverlay
          || `color-mix(in srgb, ${branding.backgroundColor} 78%, transparent)`,
      );
    } else {
      root.removeAttribute("data-bg-image");
      root.style.removeProperty("--brand-bg-image");
      root.style.removeProperty("--brand-bg-overlay");
    }

    // Document title + favicon
    document.title = branding.name;
    if (branding.faviconUrl) {
      let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = branding.faviconUrl;
    }
  }, [
    branding.name,
    branding.primaryColor,
    branding.secondaryColor,
    branding.accentColor,
    branding.backgroundColor,
    branding.surfaceColor,
    branding.fontFamily,
    branding.radius,
    branding.useGradients,
    branding.backgroundImageUrl,
    branding.backgroundImageOverlay,
    branding.faviconUrl,
    theme,
  ]);

  return branding;
}

/** Call this from anywhere after PUT /api/settings/business succeeds. */
export function useInvalidateBranding(): () => void {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: BRANDING_QUERY_KEY });
}
